import { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function EmailPreferences() {
  const { user, setUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const toggleDigest = async () => {
    setSaving(true);
    try {
      const next = !(user.digest_subscribed ?? true);
      const { data } = await api.patch("/profile", { digest_subscribed: next });
      setUser(data);
    } finally {
      setSaving(false);
    }
  };

  const sendPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      const { data } = await api.post("/digest/preview");
      setPreview(data.sent ? `Preview sent to ${user.email} · ${data.listings_count} listings included.` : "Could not send preview. Check that the mail server is configured.");
    } catch (e) {
      setPreview("Could not send preview right now.");
    } finally {
      setPreviewing(false);
    }
  };

  const subscribed = user?.digest_subscribed ?? true;

  return (
    <main className="max-w-2xl mx-auto px-6 sm:px-10 py-16">
      <Link to="/dashboard" className="label-overline hover:text-wax-red">← back to dashboard</Link>

      <p className="label-overline mt-6">Your post</p>
      <h1 className="font-display text-5xl mt-2 mb-2">Email preferences</h1>
      <div className="fleuron mb-8">❦</div>

      <div className="parchment-card mb-6">
        <p className="label-overline">Account notices</p>
        <h2 className="font-display text-2xl mt-2">Always on</h2>
        <p className="font-body text-ink-iron mt-2">
          You will always receive essential letters about your account: welcome, password resets, applications you submit or receive, and subscription receipts. These cannot be silenced — they are how the arrangement is honoured.
        </p>
      </div>

      <div className="parchment-card mb-6" data-testid="prefs-digest-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="label-overline">A Letter from the Estate</p>
            <h2 className="font-display text-2xl mt-2">Weekly digest</h2>
            <p className="font-body text-ink-iron mt-2">
              A quiet, beautifully-typeset summary of new hermitages and hermits, sent once a week. No marketing. No salesmanship. Just the bulletin.
            </p>
          </div>
          <button
            data-testid="prefs-digest-toggle"
            onClick={toggleDigest}
            disabled={saving}
            aria-pressed={subscribed}
            className={`shrink-0 mt-1 relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${subscribed ? "bg-moss" : "bg-rule"}`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-parchment-faded transition ${subscribed ? "translate-x-6" : "translate-x-1"}`} />
          </button>
        </div>
        <p className="font-body italic text-ink-marginalia text-sm mt-3">
          {subscribed ? "You are subscribed. We shall write each week." : "You are unsubscribed. No weekly letter will arrive."}
        </p>

        {subscribed && (
          <div className="mt-5 pt-5 border-t border-rule">
            <button
              data-testid="prefs-send-preview"
              onClick={sendPreview}
              disabled={previewing}
              className="ghost-button"
            >
              {previewing ? "Sending…" : "Send me a sample"}
            </button>
            {preview && <p className="font-body text-sm text-moss mt-3 italic">{preview}</p>}
          </div>
        )}
      </div>

      <p className="font-body text-ink-marginalia text-sm italic text-center">
        Sending from <strong>hello@hearthandhermit.com</strong>. Reply to any letter to reach the stewards.
      </p>
    </main>
  );
}
